import {memo, type ReactNode, Suspense} from 'react';
import {Outlet} from 'react-router-dom';

import AccountCircle from '@mui/icons-material/AccountCircle';
import ApartmentIcon from '@mui/icons-material/Apartment';
import Logout from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import {styled} from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import {AppShellBreadcrumbs} from './AppShellBreadcrumbs';
import {SideNavigation} from './SideNavigation';
import {OptixDialog} from '../../../../ui-lib/components/dialog/OptixDialog';
import {SelectOrganisationModel} from '../../../home/components/SelectOrganisationModel';
import {useAppShell} from '../../hooks/useAppShell';
import {NotificationsBell} from '../notifications/NotificationsBell';
import {PageLoader} from '../PageLoader';

interface AppShellProps {
  children?: ReactNode;
}

const Shell = styled(Stack)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
  flexDirection: 'row',
  flex: 1,
  position: 'relative',
  overflow: 'hidden !important',
}));

const ContentWrapper = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden !important',
}));

const Header = styled(Box)(({theme}) => ({
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.drawer + 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const ContentContainer = styled(Box)(({theme}) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: theme.spacing(1),
  '& > *': {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
}));

const BreadcrumbWrapper = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

function AppShellComponent({children}: AppShellProps): JSX.Element {
  const {
    handleLogout,
    isMobile,
    isNarrow,
    logoutDialogOpen,
    mobileNavOpen,
    name,
    onCloseLogoutDialog,
    onCloseSelectOrganisation,
    onGoToProfile,
    onOpenLogoutDialog,
    onOpenSelectOrganisation,
    openSelectOrganisation,
    organisationGroupName,
    organisationName,
    theme,
    toggleMobileNav,
  } = useAppShell();

  return (
    <Shell role="main" data-testid="shell">
      <SideNavigation
        isMobile={isMobile}
        mobileOpen={mobileNavOpen}
        onMobileToggle={toggleMobileNav}
      />

      <ContentWrapper sx={{marginLeft: 0}}>
        <Header>
          {isMobile ? (
            <IconButton
              color="inherit"
              onClick={toggleMobileNav}
              size="large"
              sx={{
                position: 'relative',
                zIndex: theme.zIndex.drawer + 1,
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <BreadcrumbWrapper>
              <AppShellBreadcrumbs />
            </BreadcrumbWrapper>
          )}

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flex: '0 1 auto',
            }}
          >
            <Tooltip
              title={`${organisationName} (${organisationGroupName})`}
              arrow
            >
              <Button
                size={isMobile || isNarrow ? 'small' : 'medium'}
                color="primary"
                startIcon={<ApartmentIcon />}
                onClick={onOpenSelectOrganisation}
              >
                Organisation
              </Button>
            </Tooltip>
            <SelectOrganisationModel
              open={openSelectOrganisation}
              onClose={onCloseSelectOrganisation}
            />
            <Tooltip title={name} arrow>
              <Button
                size={isMobile || isNarrow ? 'small' : 'medium'}
                color="primary"
                startIcon={<AccountCircle />}
                onClick={onGoToProfile}
                sx={{pr: isMobile ? 1 : 0}}
              >
                Profile
              </Button>
            </Tooltip>
            <NotificationsBell />
            {!isMobile && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={<Logout />}
                onClick={onOpenLogoutDialog}
                sx={{px: 1}}
              >
                Logout
              </Button>
            )}
            <OptixDialog
              open={logoutDialogOpen}
              onClose={onCloseLogoutDialog}
              title="Confirm Logout"
              message="Are you sure you want to log out?"
              enableAccept
              acceptText="Logout"
              acceptProps={{color: 'error'}}
              onAccept={handleLogout}
              enableDeny
              denyText="Cancel"
              maxWidth="sm"
              fullWidth
            />
          </Box>
        </Header>

        <ContentContainer data-testid="contentContainer">
          <Suspense fallback={<PageLoader message="Preparing content" />}>
            {children ?? <Outlet />}
          </Suspense>
        </ContentContainer>
      </ContentWrapper>
    </Shell>
  );
}

export const AppShell = memo(AppShellComponent);
